import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { useForceCheck, useMomoLive } from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";

function ageColor(ageSec: number): string {
	if (ageSec < 30) return "success";
	if (ageSec < 120) return "warning";
	return "danger";
}

function fmtAge(ageSec: number): string {
	if (ageSec < 60) return `${ageSec}s`;
	const m = Math.floor(ageSec / 60);
	const s = ageSec % 60;
	return `${m}m ${s}s`;
}

export function AdminMomoLivePage() {
	const navigate = useNavigate();
	const { data, refetch, isFetching } = useMomoLive(true);
	const forceCheck = useForceCheck();

	const pending = data?.pending ?? [];
	const k = data?.kpis ?? {
		success_last5m: 0,
		failures_last1h: 0,
		today_success_amount: 0,
		today_success_count: 0,
	};

	return (
		<>
			<PageHead
				title="MoMo / OM — Live"
				sub="Polling auto 5 s · vert <30 s, orange 30 s – 2 min, rouge >2 min"
				actions={
					<>
						<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
							<AdminIcon n="chevL" s={14} />
							Retour
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() => refetch()}
							disabled={isFetching}
						>
							<AdminIcon n="refresh" s={14} />
							Rafraîchir
						</AdminButton>
					</>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi success">
					<div className="kpi-l">Succès dernières 5 min</div>
					<div className="kpi-v tn">{k.success_last5m}</div>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Échecs dernière heure</div>
					<div className="kpi-v tn">{k.failures_last1h}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Aujourd'hui · count</div>
					<div className="kpi-v tn">{k.today_success_count}</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Aujourd'hui · montant</div>
					<div className="kpi-v tn">{fmtFCFA(k.today_success_amount)}</div>
				</div>
			</div>

			<Card>
				<CardHead
					title={`Transactions en attente (${pending.length})`}
					sub="Action « Force-check » : interroge le PSP pour rattraper un webhook manqué"
				/>
				{pending.length === 0 ? (
					<CardBody>
						<div className="empty">Aucune transaction en attente.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Référence</th>
									<th>Méthode</th>
									<th className="right">Montant</th>
									<th>MSISDN</th>
									<th>Âge</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{pending.map((p) => {
									const c = ageColor(p.age_seconds);
									return (
										<tr key={p.id}>
											<td>
												<span className="mono">{p.reference}</span>
											</td>
											<td>{p.method}</td>
											<td className="right tn">
												<strong>{fmtFCFA(p.amount)}</strong>
											</td>
											<td className="mono">{p.msisdn ?? "—"}</td>
											<td>
												<span
													className={`tag tag-${c}`}
													style={{
														padding: "3px 10px",
														borderRadius: 6,
														fontWeight: 600,
													}}
												>
													{fmtAge(p.age_seconds)}
												</span>
											</td>
											<td>
												<AdminButton
													variant="ghost"
													onClick={() =>
														forceCheck.mutate(p.id, {
															onSuccess: () => refetch(),
															onError: (e) => {
																const err = e as {
																	response?: { data?: { message?: string } };
																	message: string;
																};
																alert(
																	err.response?.data?.message ?? err.message,
																);
															},
														})
													}
													disabled={forceCheck.isPending}
												>
													<AdminIcon n="refresh" s={12} />
													Force-check
												</AdminButton>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	);
}
